import { useState, Fragment } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { WeekDay } from "Types/WeekDay";
import type { HoursState, DayHours } from "Types/OpeningHours";
import {
  formatDayHours,
  normalizeEmptyRangesToClosed,
} from "Utils/OpeningHoursUtils";
import { Resources, useResource } from "Translations/Resources";
import { DayHoursRow } from "./DayHoursRow/DayHoursRow";
import { DayEditActions } from "./DayEditActions";
import { checkTimeRangesValidity } from "Utils/TimeUtils";

type Props = {
  state: HoursState;
  onDayChange: (day: WeekDay, dayHours: DayHours) => void;
};

const ORDERED_DAYS = [
  WeekDay.Monday,
  WeekDay.Tuesday,
  WeekDay.Wednesday,
  WeekDay.Thursday,
  WeekDay.Friday,
  WeekDay.Saturday,
  WeekDay.Sunday,
];

export const OpeningHoursSummary = ({ state, onDayChange }: Props) => {
  const { t } = useResource();
  const [editingDay, setEditingDay] = useState<WeekDay | null>(null);
  const [tempDayHours, setTempDayHours] = useState<DayHours | null>(null);

  const isDayHoursValid = checkTimeRangesValidity(tempDayHours?.ranges || []);

  const handleStartEdit = (day: WeekDay) => {
    setEditingDay(day);
    setTempDayHours(state[day]);
  };

  const handleSave = () => {
    if (editingDay && tempDayHours) {
      const finalDayHours = normalizeEmptyRangesToClosed(tempDayHours);
      onDayChange(editingDay, finalDayHours);
    }
    setEditingDay(null);
    setTempDayHours(null);
  };

  const handleCancel = () => {
    setEditingDay(null);
    setTempDayHours(null);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto auto 1fr",
          gap: 2,
          rowGap: 1,
          alignItems: "center",
        }}
      >
        {ORDERED_DAYS.map((day) => {
          const dayHours = state[day];
          const isEditing = editingDay === day;

          if (isEditing && tempDayHours) {
            return (
              <Box key={`${day}-editor`} sx={{ gridColumn: "1 / -1", mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t(Resources.Common.WeekDay[day])}
                </Typography>
                <DayHoursRow
                  dayHours={tempDayHours}
                  onChange={setTempDayHours}
                />
                <DayEditActions
                  onSave={handleSave}
                  onCancel={handleCancel}
                  hasError={!isDayHoursValid}
                />
              </Box>
            );
          }

          const formattedHours = formatDayHours(dayHours, t);

          return (
            <Fragment key={day}>
              <IconButton
                size="small"
                onClick={() => handleStartEdit(day)}
                aria-label={`Edit ${t(Resources.Common.WeekDay[day])}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t(Resources.Common.WeekDay[day])}:
              </Typography>
              <Typography variant="body2">{formattedHours}</Typography>
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
};
